/**
 * Ship specifications — data only, shared by server and browser.
 *
 * A spec is everything the simulation needs to step a hull and its turrets:
 * dimensions for the hit box, kinematics for `stepShip`, gunnery for
 * `spawnShell`. Three stock classes, one per line of the tech tree; the
 * shipyard's hull items are overrides on top of them (`buildShipSpec`).
 *
 * ── What the three lines are for ─────────────────────────────────────────────
 *
 * Destroyer — torpedoes, concealment, speed, and a hull that cannot take a
 * heavy shell. It is the only class that can decline a fight it is losing, and
 * the only one whose weapon does not care how thick the target is.
 *
 * Cruiser — the all-rounder, and the only line that changes character without
 * changing id: the first two hulls are light (handy, quick to answer the
 * rudder), the last two are heavy (armoured, sluggish off the mark, worth a
 * triple mounting). Nothing about that split is in the data except the
 * numbers; there is no `light`/`heavy` flag to keep in step.
 *
 * Battleship — armour, reach and patience. Muzzle velocity 320 m/s puts a
 * ballistic maximum near 10 km against a 7 km arena, which is the whole point
 * of the class: a battleship can reach anywhere on the map from anywhere on
 * the map, and pays for it with a turn rate half a cruiser's, a slew rate that
 * loses a destroyer inside 3 km, and a silhouette visible from 6–7 km.
 *
 * ── What a tier costs ────────────────────────────────────────────────────────
 *
 * A tier is never a strict superset of the one below it — a hull that is
 * better at everything makes the hull below it dead weight in the catalogue.
 * Each line pays a different currency for its tier:
 *
 * - Destroyer pays *handling and torpedo reload*. Tier III and IV are faster,
 *   tougher and carry more tubes, but they answer the rudder worse every tier
 *   and wait longer between fans. A tier IV destroyer commits harder.
 * - Cruiser pays *turn rate and acceleration*. The heavy half of the line has
 *   half again the hit points of the light half and is markedly worse at
 *   getting out of the way of a torpedo it saw late.
 * - Battleship pays *turn rate and acceleration too, from an already terrible
 *   base*. Tier IV is 60 % more ship than tier I and manoeuvres like a pier.
 *
 * Concealment improves along every line, which looks like a free gain until
 * you notice it is the only stat that does: it is the thread that makes a tier
 * IV hull worth sailing at all once its handling has been paid away.
 *
 * Units: metres, seconds, radians. `yaw` 0 points along +Z and grows clockwise
 * seen from above, so forward = (sin yaw, cos yaw) and right = (cos yaw, −sin yaw).
 */

/** The three stock classes. Bots sail these; a player's loadout builds on one. */
export type ShipSpecId = 'destroyer' | 'cruiser' | 'battleship';
/** Which stock class a hull is built on: geometry, mounts, hit box. */
export type ShipClassId = ShipSpecId;
/**
 * Which GLB a hull is drawn with — a plain string, not a class.
 *
 * Every hull item in the catalogue names its own model (`destroyer_t3` draws
 * `destroyer_t3`), so twelve hulls are twelve silhouettes rather than three
 * repeated four times. It is deliberately not a union: the renderer keeps a
 * table of the models it actually has and falls back to a grey stand-in for an
 * id it does not know, so a hull whose file has not been made yet degrades to
 * a placeholder instead of failing to compile or failing to draw.
 */
export type ShipModelId = string;

/**
 * The arc a mount can train through, in hull-relative radians. `centre` is
 * where the turret rests (0 = bow, π = stern); `halfArc` is how far either way
 * it may go. The complement is the ship's own superstructure, and a turret is
 * never allowed to point through it — `stepShip` clamps to the arc and walks
 * the legal way round, and the arena will not fire a mount that cannot bear.
 */
export interface YawLimits {
  centre: number;
  halfArc: number;
}

export interface TurretSpec {
  /** Turret pivot in hull space: `x` to starboard, `z` toward the bow. */
  x: number;
  z: number;
  yawLimits: YawLimits;
  /** Radians per second the turret can train. */
  slewRate: number;
  /** Seconds between salvos from this turret. */
  reload: number;
  /** Muzzle velocity, metres per second. Decides the maximum range. */
  shellSpeed: number;
  /** Muzzle distance from the pivot, metres. */
  barrelLength: number;
  /** Half-angle of the bearing scatter, radians. */
  dispersion: number;
  /** Base damage of one shell from this mount; the hit zone multiplies it (`damage.ts`). */
  shellDamage: number;
  /** Turret GLB to draw, when not the class default. */
  modelId?: string;
  /** CSS-style hex tint for a premium mount, applied by the renderer. */
  tint?: string;
}

/**
 * The fish itself: what leaves the tube, once it is in the water.
 *
 * A torpedo is a slow, blind, one-way weapon. It cannot be steered after it is
 * launched, it takes minutes to cross the ranges guns cross in seconds, and it
 * runs out of fuel and sinks. What it buys for all that is the only hit in the
 * game a small hull can land on a large one and feel: a warhead below the
 * waterline, which is a flooding hit at a size no gun on a destroyer will ever
 * reach (`damage.ts`).
 *
 * `armingDistance` is the safety the real weapon has and the game needs for
 * the same reason: a warhead live in the tube would let a captain sink himself,
 * and a torpedo launched into a hull alongside would be a melee weapon rather
 * than a naval one.
 */
export interface TorpedoSpec {
  /** Metres per second. A third of a shell's, which is the whole character of the weapon. */
  speed: number;
  /** Metres of fuel. Past it the fish sinks without exploding. */
  range: number;
  /** Metres before the warhead is live. Inside it a torpedo cannot hurt anything. */
  armingDistance: number;
  /** Base damage of one fish; the zone and the flood roll decide the rest (`damage.ts`). */
  damage: number;
}

/**
 * A launcher. Broadside by nature: a bank of tubes on one side of the hull,
 * covering roughly abeam, unable to train through its own superstructure — so
 * a destroyer that wants to use them has to show a flank, and choosing *which*
 * flank is the decision the weapon is made of.
 */
export interface TorpedoMountSpec extends TorpedoSpec {
  /** Launcher position in hull space: `x` to starboard, `z` toward the bow. */
  x: number;
  z: number;
  yawLimits: YawLimits;
  /** Seconds between salvos from this mount. */
  reload: number;
  /** Fish per salvo. */
  tubes: number;
  /** Half-angle of the fan the salvo is spread over, radians. */
  spread: number;
  /**
   * Launcher GLB to draw. A triple bank and a quintuple bank are visibly
   * different objects on the deck, and the tier the captain paid for should be
   * the one an enemy can count. Nothing renders these yet; the id is the slot.
   */
  modelId?: string;
}

export interface HullBox {
  /** Half the hit box length along the keel. */
  halfLength: number;
  /** Half the hit box width. */
  halfBeam: number;
  /** How far the hit box reaches below the waterline. */
  draft: number;
  /** How far it reaches above — deck plus superstructure. */
  height: number;
}

export interface ShipSpec {
  /**
   * A stock class id for bots, or the hull item id for a player's loadout.
   * Never trust it to look anything up client-side — `classId` and `modelId`
   * are what the renderer keys on.
   */
  id: string;
  classId: ShipClassId;
  modelId: ShipModelId;
  name: string;
  lengthM: number;
  beamM: number;
  /** Top speed ahead, m/s. */
  maxSpeed: number;
  /** Astern top speed as a fraction of `maxSpeed`. */
  astern: number;
  /** Speed gained per second when the engines drive. */
  accel: number;
  /** Speed lost per second when they ease off (drag does the work, so faster). */
  decel: number;
  /** Yaw rate at full rudder and full speed, rad/s. Scales with speed. */
  turnRate: number;
  /** How fast the rudder itself moves, in full deflections per second. */
  rudderResponse: number;
  hp: number;
  /**
   * The range, in metres, at which an enemy hull picks this one out of the
   * sea. Lower is better — it is the one stat a player wants *down*. The rule
   * itself is in `vision.ts`; the spec only carries the number.
   */
  concealment: number;
  hull: HullBox;
  turrets: readonly TurretSpec[];
  /** Torpedo launchers, when the class carries any. Absent means it does not. */
  torpedoes?: readonly TorpedoMountSpec[];
  /** Repair party charges per life; `damage.ts` defaults when absent. */
  repairCharges?: number;
  /** Multiplies how long fires and flooding last; 1 when absent. */
  statusDurationMult?: number;
}

export const SHIP_SPECS: Readonly<Record<ShipSpecId, ShipSpec>> = {
  destroyer: {
    id: 'destroyer',
    classId: 'destroyer',
    modelId: 'destroyer',
    name: 'Destroyer',
    lengthM: 110,
    beamM: 21,
    maxSpeed: 18,
    astern: 0.4,
    accel: 1.5,
    decel: 2.1,
    turnRate: 0.22,
    rudderResponse: 1.6,
    hp: 14_000,
    concealment: 2_600,
    hull: { halfLength: 55, halfBeam: 10.5, draft: 3.5, height: 8 },
    turrets: [
      { x: 0, z: 42, yawLimits: { centre: 0, halfArc: 0.75 * Math.PI }, slewRate: 0.9, reload: 4, shellSpeed: 260, barrelLength: 8, dispersion: 0.012, shellDamage: 900 },
      { x: 0, z: -47, yawLimits: { centre: Math.PI, halfArc: 0.75 * Math.PI }, slewRate: 0.9, reload: 4, shellSpeed: 260, barrelLength: 8, dispersion: 0.012, shellDamage: 900 },
    ],
    // One bank a side, amidships. `halfArc` 1.2 rad (69°) about the beam
    // leaves each launcher covering from 21° off the bow to 21° off the
    // stern on its own side and nothing at all across the keel, so a captain
    // who wants to launch must first decide which flank to show.
    //
    // 6 km of fuel against a 2.6 km concealment: the fish outrange the eyes
    // that guide them, which is why a torpedo is a guess about where a ship
    // will be rather than a shot at where it is. Sixty seconds between salvos
    // makes each one a commitment; 30 m/s means a target 3 km away has a
    // hundred seconds to sail out of the fan.
    torpedoes: [
      { x: 6, z: -4, yawLimits: { centre: Math.PI / 2, halfArc: 1.2 }, reload: 60, tubes: 3, spread: 0.03, speed: 30, range: 6_000, armingDistance: 200, damage: 5_500, modelId: 'tubes_triple' },
      { x: -6, z: -4, yawLimits: { centre: -Math.PI / 2, halfArc: 1.2 }, reload: 60, tubes: 3, spread: 0.03, speed: 30, range: 6_000, armingDistance: 200, damage: 5_500, modelId: 'tubes_triple' },
    ],
  },
  cruiser: {
    id: 'cruiser',
    classId: 'cruiser',
    modelId: 'cruiser',
    name: 'Cruiser',
    lengthM: 180,
    beamM: 27,
    maxSpeed: 15,
    astern: 0.35,
    accel: 1.0,
    decel: 1.5,
    turnRate: 0.14,
    rudderResponse: 1.0,
    hp: 28_000,
    concealment: 4_200,
    hull: { halfLength: 90, halfBeam: 13.5, draft: 6, height: 9.5 },
    turrets: [
      { x: 0, z: 54, yawLimits: { centre: 0, halfArc: 0.75 * Math.PI }, slewRate: 0.6, reload: 7, shellSpeed: 280, barrelLength: 12, dispersion: 0.01, shellDamage: 1_400 },
      { x: 0, z: 36, yawLimits: { centre: 0, halfArc: 0.75 * Math.PI }, slewRate: 0.6, reload: 7, shellSpeed: 280, barrelLength: 12, dispersion: 0.01, shellDamage: 1_400 },
      { x: 0, z: -34, yawLimits: { centre: Math.PI, halfArc: 0.75 * Math.PI }, slewRate: 0.6, reload: 7, shellSpeed: 280, barrelLength: 12, dispersion: 0.01, shellDamage: 1_400 },
    ],
  },
  // A 220 m hull with three heavy mountings and nothing else to offer.
  //
  // 320 m/s of muzzle velocity is the number the class is built around: a flat
  // ballistic maximum of v²/g ≈ 10.4 km against a 7 km arena, so there is no
  // corner of the map a battleship cannot reach from any other. Everything
  // else is the bill for it — 11 m/s, a turn rate half the cruiser's, a slew
  // rate of a quarter radian a second that cannot hold a destroyer inside
  // 3 km, and 6.8 km of concealment, which means it is seen long before it
  // sees. Reload 28 s makes a salvo a decision rather than a stream, and
  // 4 200 a shell makes a full broadside worth about a destroyer and a half if
  // every round lands, which it will not.
  battleship: {
    id: 'battleship',
    classId: 'battleship',
    modelId: 'battleship',
    name: 'Battleship',
    lengthM: 220,
    beamM: 32,
    maxSpeed: 11,
    astern: 0.3,
    accel: 0.6,
    decel: 1.1,
    turnRate: 0.07,
    rudderResponse: 0.6,
    hp: 60_000,
    concealment: 6_800,
    hull: { halfLength: 110, halfBeam: 16, draft: 9, height: 12.5 },
    turrets: [
      { x: 0, z: 66, yawLimits: { centre: 0, halfArc: 0.72 * Math.PI }, slewRate: 0.25, reload: 28, shellSpeed: 320, barrelLength: 18, dispersion: 0.009, shellDamage: 4_200 },
      { x: 0, z: 44, yawLimits: { centre: 0, halfArc: 0.72 * Math.PI }, slewRate: 0.25, reload: 28, shellSpeed: 320, barrelLength: 18, dispersion: 0.009, shellDamage: 4_200 },
      { x: 0, z: -58, yawLimits: { centre: Math.PI, halfArc: 0.72 * Math.PI }, slewRate: 0.25, reload: 28, shellSpeed: 320, barrelLength: 18, dispersion: 0.009, shellDamage: 4_200 },
    ],
  },
};

export const DEFAULT_SHIP_SPEC_ID: ShipSpecId = 'destroyer';

export const SHIP_SPEC_IDS: readonly ShipSpecId[] = ['destroyer', 'cruiser', 'battleship'];

export function isShipSpecId(id: unknown): id is ShipSpecId {
  return typeof id === 'string' && (SHIP_SPEC_IDS as readonly string[]).includes(id);
}

/** The spec for an id, or the default for anything unknown — never throws. */
export function shipSpec(id: unknown): ShipSpec {
  return SHIP_SPECS[isShipSpecId(id) ? id : DEFAULT_SHIP_SPEC_ID];
}

// ─── Loadouts ────────────────────────────────────────────────────────────────
//
// The shipyard sells items whose stats are plain JSON; `buildShipSpec` folds a
// hull, its turrets and up to three modules into one `ShipSpec` the arena can
// step. Pure and deterministic, so the server builds it at join time and a
// test can pin every number. Everything is an override or a multiplier over
// the class's stock spec — an item never has to repeat the geometry.

/**
 * What a hull item changes about its class's launchers, applied to every mount.
 *
 * Tubes are the hull's — nothing sells a bank of launchers — but a tier IV
 * destroyer carries five tubes a side where a tier I carries three, and that
 * difference belongs to the hull that was bought rather than to a module.
 * Every field is an override, so a tier that changes nothing says nothing.
 */
export interface HullTorpedoStats {
  /** Fish per salvo, per mount. */
  tubes?: number;
  damage?: number;
  reload?: number;
  range?: number;
  speed?: number;
  /** Launcher GLB; the class's mount decides when absent. */
  modelId?: string;
}

/** A hull item's stats. `classId` picks the stock spec it overrides. */
export interface HullItemStats {
  classId: ShipClassId;
  hp?: number;
  maxSpeed?: number;
  accel?: number;
  turnRate?: number;
  /** Detection range in metres; lower is better. Stock class value when absent. */
  concealment?: number;
  /**
   * Hull GLB to draw. Every catalogue hull names its own, so the tier a
   * captain paid for is the shape on the water; the class's model is the
   * fallback for a hull that has not been given one.
   */
  modelId?: string;
  /**
   * The mounting the hull is delivered with, which always fits her; the class
   * stock gun (`twin_120`) when absent. A premium hull has no research bush to
   * grant her a gun, so she names the one she ships with here.
   */
  stockTurretId?: string;
  /** Tier changes to the class's tubes; nothing at all on a hull without any. */
  torpedoes?: HullTorpedoStats;
}

/** A turret item's stats. Geometry (mount position, arc) always comes from the hull. */
export interface TurretItemStats {
  reload?: number;
  shellDamage?: number;
  shellSpeed?: number;
  dispersion?: number;
  slewRate?: number;
  modelId?: string;
  tint?: string;
}

/**
 * A module's stats: multipliers over the built spec, or a repair charge.
 *
 * Since phase 3 ("research per hull") this shape is also what a researched
 * node's `statDelta` folds into — Hull B/Propulsion/Fire Control/Torpedoes B
 * are, mechanically, modules a hull always has fitted once earned. Nothing
 * about `buildShipSpec` needed to change to accept them: they go into the same
 * `modules` array a player's own picks do, appended rather than counted
 * against `MAX_MODULES` (see `ShipyardService.loadoutSpecFor`).
 */
export interface ModuleItemStats {
  speedMult?: number;
  turnMult?: number;
  /**
   * Multiplies the hull's detection range. Below 1 is the good direction, the
   * opposite of every other multiplier here — concealment is the one stat a
   * captain wants smaller, so a camouflage module reads `0.88`, not `1.12`.
   */
  concealmentMult?: number;
  dispersionMult?: number;
  reloadMult?: number;
  /** Hull B's HP bonus and Fire Control's shell-speed bonus, folded the same way. */
  hpMult?: number;
  accelMult?: number;
  shellSpeedMult?: number;
  /**
   * The torpedo battery, for the hulls that carry one. A module that touches
   * tubes does nothing at all on a ship without them, which is deliberate: a
   * cruiser captain who buys one has bought a paperweight, and the blurb says
   * so rather than the code pretending otherwise.
   */
  torpedoDamageMult?: number;
  torpedoReloadMult?: number;
  torpedoSpeedMult?: number;
  statusDurationMult?: number;
  extraRepairCharges?: number;
}

export const MAX_MODULES = 3;

/**
 * Builds the spec a player actually sails.
 *
 * `turrets[i]` is the item on mount `i` (null = the class's stock gun). A hull
 * always has exactly its class's mounts; extra entries are ignored and
 * missing ones fall back to stock. Modules multiply after everything else so
 * their order never matters.
 *
 * Item levels retired with research-per-hull (phase 3): a hull or turret's
 * stat bump is now a one-time researched node rather than three purchasable
 * pips, so there is no `levels` parameter any more — a node's bonus is folded
 * into `modules` by the caller instead, exactly like a player's own fitted one.
 */
export function buildShipSpec(
  id: string,
  name: string,
  hull: HullItemStats,
  turrets: readonly (TurretItemStats | null)[],
  modules: readonly ModuleItemStats[],
): ShipSpec {
  const base = SHIP_SPECS[hull.classId];
  const mods = modules;
  const mult = (key: keyof ModuleItemStats): number =>
    mods.reduce((acc, m) => acc * (typeof m[key] === 'number' && m[key]! > 0 ? m[key]! : 1), 1);
  const extraCharges = mods.reduce((acc, m) => acc + Math.max(0, Math.floor(m.extraRepairCharges ?? 0)), 0);

  const hp = Math.round((hull.hp ?? base.hp) * mult('hpMult'));
  const maxSpeed = (hull.maxSpeed ?? base.maxSpeed) * mult('speedMult');
  const turnRate = (hull.turnRate ?? base.turnRate) * mult('turnMult');
  const dispersionMult = mult('dispersionMult');
  const reloadMult = mult('reloadMult');
  const shellSpeedMult = mult('shellSpeedMult');
  const concealment = (hull.concealment ?? base.concealment) * mult('concealmentMult');

  const builtTurrets: TurretSpec[] = base.turrets.map((stock, i) => {
    const item = turrets[i] ?? null;
    const shellDamage = Math.round(item?.shellDamage ?? stock.shellDamage);
    const t: TurretSpec = {
      x: stock.x,
      z: stock.z,
      yawLimits: stock.yawLimits,
      barrelLength: stock.barrelLength,
      slewRate: item?.slewRate ?? stock.slewRate,
      reload: (item?.reload ?? stock.reload) * reloadMult,
      shellSpeed: (item?.shellSpeed ?? stock.shellSpeed) * shellSpeedMult,
      dispersion: (item?.dispersion ?? stock.dispersion) * dispersionMult,
      shellDamage,
    };
    if (item?.modelId) t.modelId = item.modelId;
    if (item?.tint) t.tint = item.tint;
    return t;
  });

  const spec: ShipSpec = {
    id,
    classId: base.classId,
    modelId: hull.modelId ?? base.modelId,
    name,
    lengthM: base.lengthM,
    beamM: base.beamM,
    maxSpeed,
    astern: base.astern,
    accel: (hull.accel ?? base.accel) * mult('accelMult'),
    decel: base.decel,
    turnRate,
    rudderResponse: base.rudderResponse,
    hp,
    concealment,
    hull: base.hull,
    turrets: builtTurrets,
  };
  // Launchers are the hull's — nothing sells a bank of tubes, only the things
  // that go in them — so a loadout carries its class's mounts with the module
  // multipliers folded in, rather than silently losing them. A ship with no
  // tubes is unaffected by every one of those multipliers.
  if (base.torpedoes) {
    const torpedoDamage = mult('torpedoDamageMult');
    const torpedoReload = mult('torpedoReloadMult');
    const torpedoSpeed = mult('torpedoSpeedMult');
    const tier = hull.torpedoes;
    const untouched = !tier && torpedoDamage === 1 && torpedoReload === 1 && torpedoSpeed === 1;
    spec.torpedoes = untouched
      ? base.torpedoes
      : base.torpedoes.map((m) => ({
          ...m,
          tubes: tier?.tubes ?? m.tubes,
          range: tier?.range ?? m.range,
          modelId: tier?.modelId ?? m.modelId,
          damage: Math.round((tier?.damage ?? m.damage) * torpedoDamage),
          reload: (tier?.reload ?? m.reload) * torpedoReload,
          speed: (tier?.speed ?? m.speed) * torpedoSpeed,
        }));
  }
  const durationMult = mult('statusDurationMult');
  if (durationMult !== 1) spec.statusDurationMult = durationMult;
  if (extraCharges > 0) spec.repairCharges = 3 + extraCharges;
  return spec;
}
